import React, { useState, useEffect, useMemo, useRef } from 'react'
import DataTable, { activeStyle, inactiveStyle } from '../Shared/DataTable/DataTable.js'
import * as FieldTypes from '../Shared/FieldTypes/FieldTypes.js'
import ErrorMessage from '../Shared/Error.js'
import LoadingSpinnerImage from '../Shared/LoadingSpinnerImage.js'
import { getBasicUDIsConfig } from '../BasicUDIs/BasicUDIs.js'
import BasicUDIsItemDetailsPane from '../BasicUDIs/ItemDetailsPane.js'
import BasicUDIsAddItemPopup from '../BasicUDIs/AddItemPopup.js'
import AddItemPopup from './AddItemPopup.js'
import { UDI_REGIONS } from './UdiRegions.js'

const VIEW_UDIS = "udis";
const VIEW_BASIC_UDIS = "basic-udis";
const VIEW_OPTIONS = [{ value: VIEW_BASIC_UDIS, label: "Basic UDI-DIs" }, { value: VIEW_UDIS, label: "UDI-DIs" }];
const PRODUCTS_URL = "/your-medboard/products/";
const BREADCRUMBS = [{ "Name": "Products", "Link": "/your-medboard/products/" }, { "Name": "UDIs", "Link": "/your-medboard/product-portfolio/basic-udis/" }];


const getInitialSelection = () => {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view") === VIEW_BASIC_UDIS || window.preloadBasicUDIId ? VIEW_BASIC_UDIS : VIEW_UDIS;
    const region = UDI_REGIONS.find(r => r.value === params.get("region")) ?? UDI_REGIONS[0];
    return { view, region: region.value, allVersions: params.get("allVersions") === "1" };
};


const buildUdiConfig = (regionConfig, columns, filterBarControls, allVersions) => {

    let config = {};

    config.dataUrl = regionConfig.dataUrl;
    config.pageTitle = "UDIs";
    config.singleItemTitle = "UDI";
    config.entityType = regionConfig.entityType;
    config.entityTypeId = regionConfig.entityTypeId;
    config.pageIcon = "fas fa-barcode";
    config.breadcrumbs = BREADCRUMBS;
    config.defaultOrderByField = "SKUProductCode";
    config.enableCustomFields = true;
    config.disableCustomFieldCreation = true;
    config.showLegalManufacturerFilter = true;
    config.currentLegalManufacturerFilter = window.legalManufacturerId;
    config.currentLegalManufacturerNameFilter = window.legalManufacturerName;
    config.showListTotalCount = true;
    config.listTotalCountURL = regionConfig.countUrl;
    config.eudamedXmlServices = regionConfig.eudamedXmlServices;
    config.showExportButton = regionConfig.eudamedXmlServices.length > 0 && window.userRole === "Admin";
    config.hideExcelExport = true;
    config.filterBarControls = filterBarControls;
    config.extraDataUrlParams = regionConfig.hasVersions ? { allVersions } : {};

    // Opens the product's EU/US UDI tab (and the row's version) in a new browser tab
    config.customRowClickFunction = ({ rowId, row }) => {
        const productId = row?.ProductId ?? rowId;
        const versionParam = regionConfig.hasVersions ? `&udiVersionId=${encodeURIComponent(rowId)}` : "";
        window.open(`${PRODUCTS_URL}?productId=${encodeURIComponent(productId)}&productTab=udis&udiTab=${regionConfig.productUdiTab}${versionParam}`, "_blank");
    };

    config.dataStructure = columns.map(column => ({
        customFieldId: column.CustomFieldId,
        customFieldType: column.CustomFieldType,
        name: column.Name,
        field: column.Field,
        width: column.Width,
        sortable: column.Sortable,
        isSelectItemCheckbox: column.IsSelectItemCheckbox,
        renderWith: FieldTypes[column.RenderWith] ?? FieldTypes.FieldType_Text,
        isMultiField: column.IsMultiField,
        disableRowClick: column.DisableRowClick,
        hasFilter: column.HasFilter,
    }));

    return config;
};


const UdiViewControls = ({ view, region, allVersions, onViewChange, onRegionChange, onAllVersionsChange }) => (

    <div style={{ marginLeft: "15px", display: "flex", alignItems: "center", gap: "4px" }}>

        {VIEW_OPTIONS.map(option => (
            <span
                key={option.value}
                onClick={() => onViewChange(option.value)}
                style={view === option.value ? activeStyle : inactiveStyle}
            >
                <i
                    className={view === option.value ? "fas fa-check-square" : "far fa-square"}
                    style={{ marginRight: '6px', fontSize: '12px' }}
                ></i>
                {option.label}
            </span>
        ))}

        {view === VIEW_UDIS && (
            <select
                value={region}
                onChange={(e) => onRegionChange(e.target.value)}
                style={{ ...inactiveStyle, marginLeft: "11px" }}
            >
                {UDI_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
        )}

        {view === VIEW_UDIS && UDI_REGIONS.find(r => r.value === region)?.hasVersions && (
            <span
                onClick={() => onAllVersionsChange(!allVersions)}
                style={{ ...(allVersions ? activeStyle : inactiveStyle), marginLeft: "11px" }}
                title="Show every version of each UDI instead of only the current one"
            >
                <i
                    className={allVersions ? "fas fa-check-square" : "far fa-square"}
                    style={{ marginRight: '6px', fontSize: '12px' }}
                ></i>
                Show all versions
            </span>
        )}

    </div>
);


const UDIs = () => {

    const [initialSelection] = useState(getInitialSelection);
    const [view, setView] = useState(initialSelection.view);
    const [region, setRegion] = useState(initialSelection.region);
    const [allVersions, setAllVersions] = useState(initialSelection.allVersions);
    const [udiHeaders, setUdiHeaders] = useState(null);
    const [errorLoadingStructure, setErrorLoadingStructure] = useState(false);
    const structureRequestId = useRef(0);

    const regionConfig = UDI_REGIONS.find(r => r.value === region);
    const isUdiView = view === VIEW_UDIS;


    // Keep the selection in the URL so reloads and links reopen it
    useEffect(() => {
        const url = new URL(window.location);
        url.searchParams.set("view", view);
        if (isUdiView)
            url.searchParams.set("region", region);
        else
            url.searchParams.delete("region");
        if (isUdiView && regionConfig.hasVersions && allVersions)
            url.searchParams.set("allVersions", "1");
        else
            url.searchParams.delete("allVersions");
        window.history.replaceState({}, "", url);
    }, [view, region, allVersions]);


    useEffect(() => {
        if (isUdiView)
            loadStructure();
    }, [view, region]);


    const loadStructure = () => {

        const requestId = ++structureRequestId.current;
        const entityTypeId = regionConfig.entityTypeId;

        setUdiHeaders(null);
        setErrorLoadingStructure(false);

        fetch("/umbraco/api/CustomFields/Headers/?entityType=" + entityTypeId)
            .then((response) => {
                if (!response.ok)
                    throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => {
                if (requestId === structureRequestId.current)
                    setUdiHeaders({ entityTypeId, columns: result });
            })
            .catch(() => {
                if (requestId === structureRequestId.current)
                    setErrorLoadingStructure(true);
            });
    }


    const changeView = (newView) => {
        // The linked Basic UDI only opens on the first load
        window.preloadBasicUDIId = undefined;
        setView(newView);
    }


    const config = useMemo(() => {

        const controls = <UdiViewControls view={view} region={region} allVersions={allVersions} onViewChange={changeView} onRegionChange={setRegion} onAllVersionsChange={setAllVersions} />;

        if (!isUdiView)
            return { ...getBasicUDIsConfig(), filterBarControls: controls };

        // Wait for the selected region's columns
        if (udiHeaders?.entityTypeId !== regionConfig.entityTypeId)
            return null;

        return buildUdiConfig(regionConfig, udiHeaders.columns, controls, allVersions);

    }, [view, region, allVersions, udiHeaders]);


    if (isUdiView && errorLoadingStructure)
        return <ErrorMessage message={"Failed to load table configuration"} />;

    if (config == null)
        return <LoadingSpinnerImage />;

    return (
        <DataTable
            key={isUdiView ? `${region}-${allVersions}` : view}
            dataTableStructure={config}
            ItemDetailsPane={isUdiView ? null : BasicUDIsItemDetailsPane}
            AddItemPopup={isUdiView ? AddItemPopup : BasicUDIsAddItemPopup}
            ReloadStructureFunction={isUdiView ? loadStructure : null}
        />
    );

};


export default UDIs;
